import { defineStore } from 'pinia'
import { useMessageStore } from './messages'
import { useMaintenanceStore } from './maintainence'
import { useRecruitmentNotificationsStore } from './recruitmentNotifications'
import { clearAuthToken, getAuthToken, setAuthToken, VALT_API_BASE, valtFetch } from '@/utils/api'

export const useUserStore = defineStore('user', {
  state: () => ({
    id: null as number | string | null,
    name: '',
    email: '',
    memberSince: '',
    pfp: '',
    is2FAEnabled: false,
    enabled: true,
    org_id: '',
    scopes: [] as string[],
    phone: '',
    department: '',
    role: 'guest', // Default to guest
    token: getAuthToken() || '', // Load token from localStorage
  }),

  actions: {
    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    changeName(name: string) {
      this.name = name
    },

    setRole(role: string) {
      this.role = role
    },

    setToken(token: string) {
      this.token = token
      setAuthToken(token) // Save token to scoped auth storage
    },

    logout() {
      this.token = ''
      this.role = 'guest'
      clearAuthToken() // Remove token from scoped auth storage
      localStorage.removeItem('region') // Remove region from localStorage
      const messageStore = useMessageStore()
      messageStore.cleanup()
      const recruitmentStore = useRecruitmentNotificationsStore()
      recruitmentStore.disconnect()
    },

    // Async action to fetch user data from API

    async fetchUserData() {
      if (!getAuthToken() && !this.token) {
        console.error('No token found, unable to fetch user data')
        return
      }

      try {
        const response = await valtFetch(`${VALT_API_BASE}/users/logged`, {
          method: 'GET',
        })

        if (response.ok) {
          const data = await response.json()

          try {
            localStorage.setItem('userData', JSON.stringify(data))
            if (data.role) {
              localStorage.setItem('userRole', data.role)
            }
            window.dispatchEvent(new CustomEvent('userDataUpdated'))
          } catch {
            /* ignore storage */
          }

          this.id = data.id ?? null
          this.name = data.name || ''
          this.email = data.email || ''
          this.memberSince = data.memberSince || data.created_at || ''
          this.pfp = data.pfp || data.avatar || ''
          this.is2FAEnabled = Boolean(data.is2FAEnabled ?? data.two_factor_enabled)
          this.role = data.role || 'guest'
          this.enabled =
            data.enabled === undefined || data.enabled === null
              ? true
              : data.enabled === 1 || data.enabled === true || data.enabled === '1'
          this.org_id = data.org_id != null ? String(data.org_id) : ''
          this.scopes = Array.isArray(data.scopes) ? data.scopes : []
          this.phone = data.phone_number || data.phone || ''
          this.department = data.department || ''

          // Initialize Pusher after successful login
          const messageStore = useMessageStore()
          messageStore.initializePusher(
            'your_pusher_key',
            'your_pusher_cluster',
            'https://your-api-endpoint.com/pusher/auth',
          )

          const maintenanceStore = useMaintenanceStore()
          maintenanceStore.initializePusher(
            'your_pusher_key',
            'your_pusher_cluster',
            'https://your-api-endpoint.com/pusher/auth', // Might not be needed for public channel
          )
          maintenanceStore.subscribeToMaintenanceChannel()
          maintenanceStore.fetchMaintenanceUpdates()

          const recruitmentStore = useRecruitmentNotificationsStore()
          recruitmentStore.initializePusher(
            import.meta.env.VITE_PUSHER_KEY || '1e7707caf4d4eca34616',
            import.meta.env.VITE_PUSHER_CLUSTER || 'ap2',
          )

          // Subscribe to user's private channel
          messageStore.subscribeToChannel(`private-user-${data.id}`)
        } else {
          throw new Error('Failed to fetch user data')
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
        throw error
      }
    },

    // Async action for updating user name
    async updateUserName(newName: string) {
      try {
        const response = await valtFetch('/api/user/name', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ name: newName }),
        })

        if (response.ok) {
          this.name = newName
        } else {
          throw new Error('Failed to update user name')
        }
      } catch (error) {
        console.error('Error updating user name:', error)
        throw error
      }
    },

    // Async action for changing password
    async updatePassword(oldPassword: string, newPassword: string) {
      try {
        const response = await valtFetch('/api/user/password', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            oldPassword,
            newPassword,
          }),
        })

        if (response.ok) {
          return true
        } else {
          throw new Error('Failed to update password')
        }
      } catch (error) {
        console.error('Error updating password:', error)
        return false
      }
    },
  },
})
